import { useContext, useEffect, useState } from "react";
import NotificationContext from "../context/NotificationContext";
import TransactionsViewer from "./TransactionsViewer";
import { useMutationLock } from "../hooks/useMutationLock";
import {
  exportListBody,
  normalizeListResponse,
} from "../utils/listPagination";
import * as registrationsApi from "../api/registrations";
import * as coursesApi from "../api/courses";
import * as feeTransactionsApi from "../api/feeTransactions";
import * as usersApi from "../api/users";

function UserCoursesModal(props) {
  const canEdit = !!props.canEdit;
  const [usercourses, setusercourses] = useState(props.usercourses);
  const [listStatus, setListStatus] = useState(() => {
    const s = props.student;
    if (!s) return null;
    return {
      blackListed: Boolean(s.blackListed),
      greyListed: Boolean(s.greyListed),
      listReason: s.listReason || null,
    };
  });
  const [showdrop, setshowdrop] = useState(false);
  const [refund, setrefund] = useState(false);
  const [refundamount, setrefundamount] = useState(0);
  const [refundPaymentMode, setRefundPaymentMode] = useState("cash");
  const [clearRemainingPaid, setClearRemainingPaid] = useState(false);
  const [writeOffResiduals, setWriteOffResiduals] = useState(false);
  const [dropPreview, setDropPreview] = useState(null);
  const [dropPreviewLoading, setDropPreviewLoading] = useState(false);
  const [courseToDropTitle, setCourseToDropTitle] = useState("");
  const [comment, setcomment] = useState("");
  const [regtodrop, setregtodrop] = useState(null);
  const [courseToDrop, setcoursetodrop] = useState(null);
  const [showadd, setshowadd] = useState(false);
  const [regtoadd, setregtoadd] = useState(null);
  const [courseToadd, setcoursetoadd] = useState(null);
  const [addBackIsReenroll, setAddBackIsReenroll] = useState(false);
  const notificationCtx = useContext(NotificationContext);
  const { busy: mutating, run: runMutation } = useMutationLock();

  useEffect(() => {
    const fromProp = props.student;
    if (
      fromProp &&
      (fromProp.blackListed !== undefined || fromProp.greyListed !== undefined)
    ) {
      setListStatus({
        blackListed: Boolean(fromProp.blackListed),
        greyListed: Boolean(fromProp.greyListed),
        listReason: fromProp.listReason || null,
      });
      return;
    }

    const email =
      props.student?.email ||
      props.usercourses?.[0]?.email ||
      usercourses?.[0]?.email;
    if (!email) {
      setListStatus(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await usersApi.lookup({ email });
        const user = res.data?.user || res.data;
        if (cancelled || !user) return;
        setListStatus({
          blackListed: Boolean(user.blackListed),
          greyListed: Boolean(user.greyListed),
          listReason: user.listReason || null,
        });
      } catch (error) {
        if (!cancelled) setListStatus(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.student, props.usercourses]);

  const isBlackListed = Boolean(listStatus?.blackListed);
  const isGreyListed = Boolean(listStatus?.greyListed);
  const isListed = isBlackListed || isGreyListed;

  const openDropModal = async (reg, course) => {
    if (!canEdit) return;
    setcomment(course.comment || "");
    setregtodrop(reg);
    setcoursetodrop(course.id);
    setCourseToDropTitle(course.title || `Course ${course.id}`);
    setrefund(false);
    setrefundamount(0);
    setRefundPaymentMode("cash");
    setClearRemainingPaid(false);
    setWriteOffResiduals(false);
    setDropPreview(null);
    setshowdrop(true);
    setDropPreviewLoading(true);
    try {
      const res = await registrationsApi.dropPreview(reg.id, {
        courseId: course.id,
      });
      setDropPreview(res.data);
      setrefundamount(Number(res.data?.suggestedRefund) || 0);
    } catch (error) {
      console.error("Drop preview failed:", error);
      notificationCtx.show({
        message:
          error?.response?.data?.message || "Failed to load drop preview.",
        stat: "error",
      });
    } finally {
      setDropPreviewLoading(false);
    }
  };

  const dropCourse = async (courseId, registrationId) => {
    if (!canEdit) return;
    if (comment === "") {
      notificationCtx.show({
        message: "Please add a comment before proceeding.",
      });
      return;
    }
    if (refund && refundamount <= 0) {
      notificationCtx.show({
        message: "Please enter a valid refund amount.",
      });
      return;
    }
    if (refund && refundPaymentMode !== "cash" && refundPaymentMode !== "online") {
      notificationCtx.show({
        message: "Please select a payment mode for the refund.",
      });
      return;
    }
    const maxRefund = Number(dropPreview?.paidCourse || 0);
    if (refund && maxRefund > 0 && refundamount > maxRefund) {
      notificationCtx.show({
        message: `Refund cannot exceed course paid amount (PKR ${maxRefund.toLocaleString()}).`,
      });
      return;
    }

    const impact = [
      `Drop "${courseToDropTitle}" from this registration.`,
      refund
        ? `Refund PKR ${Number(refundamount).toLocaleString()} via ${refundPaymentMode} (cash ledger + teacher share).`
        : "No refund. Paid amounts stay on the books; unpaid months will be waived.",
      clearRemainingPaid
        ? "Also clear any remaining paid on this course."
        : null,
      writeOffResiduals
        ? "Also write off residual due gaps on this course."
        : null,
      "Continue?",
    ]
      .filter(Boolean)
      .join(" ");

    notificationCtx.show({
      message: impact,
      stat: "info",
      onConfirm: async () => {
        await runMutation(async () => {
          try {
            const details = {
              courseId,
              refund,
              comment,
              clearRemainingPaid,
              writeOffResiduals,
            };
            if (refund && refundamount > 0) {
              details.refundAmount = refundamount;
              details.paymentMode = refundPaymentMode;
            }
            const res = await registrationsApi.drop(registrationId, details);
            const nextStatus =
              res?.data?.status ||
              (refund ? "dropped with refund" : "dropped without refund");
            setusercourses((prev) =>
              prev.map((reg) =>
                reg.id === registrationId
                  ? {
                      ...reg,
                      courses: (reg.courses || []).map((c) =>
                        c.id === courseId
                          ? { ...c, status: nextStatus, comment }
                          : c,
                      ),
                    }
                  : reg,
              ),
            );
            setshowdrop(false);
            notificationCtx.show({
              message: "Course updated successfully",
              stat: "success",
            });
            if (typeof props.onRefresh === "function") {
              await props.onRefresh();
            }
          } catch (error) {
            console.error("Error updating course:", error);
            notificationCtx.show({
              message:
                error?.response?.data?.message || "Failed to update the course.",
              stat: "error",
            });
          }
        });
      },
    });
  };

  const addCourse = async (courseId, registrationId) => {
    if (!canEdit) return;
    if (comment === "") {
      notificationCtx.show({
        message: "Please add a comment before proceeding.",
      });
      return;
    }

    const actionLabel = addBackIsReenroll ? "Re-enroll" : "Add-back";
    const impact = addBackIsReenroll
      ? `Re-enroll this transferred course. The student may remain enrolled in the destination course as well. Prior transfer money and teacher entries are not reversed. Open dues will be restored. Continue?`
      : `Add-back this course. Open dues will be restored. Prior refunds (cash/teacher) are not clawed back. Continue?`;

    notificationCtx.show({
      message: impact,
      stat: "info",
      onConfirm: async () => {
        await runMutation(async () => {
          try {
            const details = { courseId, comment };
            await registrationsApi.addBack(registrationId, details);
            setusercourses((prev) =>
              prev.map((reg) =>
                reg.id === registrationId
                  ? {
                      ...reg,
                      courses: (reg.courses || []).map((c) =>
                        c.id === courseId
                          ? { ...c, status: "enrolled", comment }
                          : c,
                      ),
                    }
                  : reg,
              ),
            );
            setshowadd(false);
            notificationCtx.show({
              message: `${actionLabel} successful`,
              stat: "success",
            });
            if (typeof props.onRefresh === "function") {
              await props.onRefresh();
            }
          } catch (error) {
            console.error("Error updating course:", error);
            notificationCtx.show({
              message:
                error?.response?.data?.message || "Failed to update the course.",
              stat: "error",
            });
          }
        });
      },
    });
  };

  const columnKeys = [
    "Course Title",
    "Teacher",
    "Fee Status",
    "Registration Status",
    "Action",
  ];

  const [addNewCourseModalOpen, setAddNewCourseModalOpen] = useState(false);
  const [transferCourseModalOpen, setTransferCourseModalOpen] = useState(false);
  const [allActiveCourses, setAllActiveCourses] = useState([]);

  const OpenAddNewCourseModal = async () => {
    if (!canEdit) return;
    if (allActiveCourses.length === 0) {
      try {
        const response = await coursesApi.list(
          exportListBody({ active: true }),
        );
        const allActive = normalizeListResponse(response.data).data;
        const userCourseIds = new Set(
          (props.usercourses || []).flatMap((u) =>
            (u.courses || []).map((c) => c.id),
          ),
        );

        setAllActiveCourses(
          allActive.filter((course) => !userCourseIds.has(course.id)),
        );
      } catch (error) {
        console.error("Error fetching active courses:", error);
      }
    }

    setAddNewCourseModalOpen(true);
  };

  const handleAddNewCourse = async (courseId) => {
    if (!canEdit) return;
    await runMutation(async () => {
      try {
        await registrationsApi.addCourse(props.usercourses[0].id, {
          courseId: courseId,
        });
        const addedCourse = allActiveCourses.find((c) => c.id === courseId);
        if (addedCourse) {
          setusercourses((prev) =>
            prev.map((reg, index) =>
              index === 0
                ? {
                    ...reg,
                    courses: [
                      ...(reg.courses || []),
                      { ...addedCourse, status: "enrolled" },
                    ],
                  }
                : reg,
            ),
          );
          setAllActiveCourses((prev) => prev.filter((c) => c.id !== courseId));
        }
        setAddNewCourseModalOpen(false);
        notificationCtx.show({
          message: "Course added successfully",
          stat: "success",
        });
        if (typeof props.onRefresh === "function") {
          await props.onRefresh();
        }
      } catch (error) {
        console.error("Error adding new course:", error);
        setAddNewCourseModalOpen(false);
        notificationCtx.show({
          message:
            error?.response?.data?.message || "Failed to add the course.",
          stat: "error",
        });
      }
    });
  };

  const [oldCourseIdForTransfer, setOldCourseIdForTransfer] = useState(null);

  const loadTransferDestinations = async (oldCourseId) => {
    const response = await coursesApi.list(exportListBody({ active: true }));
    const allActive = normalizeListResponse(response.data).data;
    const linked = (props.usercourses || []).flatMap((u) => u.courses || []);
    const activeLinkedIds = new Set(
      linked
        .filter((c) => c.status === "enrolled" || c.status === "pending")
        .map((c) => c.id),
    );
    // Allow destinations not currently enrolled/pending (includes prior transferred/dropped).
    setAllActiveCourses(
      allActive.filter(
        (course) =>
          course.id !== oldCourseId && !activeLinkedIds.has(course.id),
      ),
    );
  };

  const OpenTransferCourseModal = async (oldCourseId) => {
    if (!canEdit) return;
    setOldCourseIdForTransfer(oldCourseId);
    try {
      await loadTransferDestinations(oldCourseId);
    } catch (error) {
      console.error("Error fetching active courses:", error);
    }
    setTransferCourseModalOpen(true);
  };

  const handleTransferCourse = async (newCourseId) => {
    if (!canEdit) return;
    const fromCourse = (props.usercourses || [])
      .flatMap((u) => u.courses || [])
      .find((c) => c.id === oldCourseIdForTransfer);
    const toCourse = allActiveCourses.find((c) => c.id === newCourseId);
    const fromTitle = fromCourse?.title || `Course ${oldCourseIdForTransfer}`;
    const toTitle = toCourse?.title || `Course ${newCourseId}`;

    notificationCtx.show({
      message: `Transfer "${fromTitle}" → "${toTitle}". The old course becomes transferred; remaining-day paid moves to the new course. Teacher A keeps the taught-day share; Teacher B receives the remaining-day share. Continue?`,
      stat: "info",
      onConfirm: async () => {
        await runMutation(async () => {
          try {
            const res = await registrationsApi.transfer(
              props.usercourses[0].id,
              {
                oldCourseId: oldCourseIdForTransfer,
                newCourseId: newCourseId,
              },
            );

            const transfer = res?.data?.transfer;
            setTransferCourseModalOpen(false);
            notificationCtx.show({
              message: transfer
                ? `Transferred. Kept PKR ${Number(transfer.keptPaid || 0).toLocaleString()}, moved PKR ${Number(transfer.movePaid || 0).toLocaleString()} (${transfer.daysRemaining}/${transfer.daysTotal} days remaining).`
                : "Course transferred successfully",
              stat: "success",
            });
            if (typeof props.onRefresh === "function") {
              await props.onRefresh();
            }
          } catch (error) {
            console.error("Error transferring course:", error);
            setTransferCourseModalOpen(false);
            notificationCtx.show({
              message:
                error?.response?.data?.message ||
                "Failed to transfer the course.",
              stat: "error",
            });
          }
        });
      },
    });
  };

  const [transactionData, setTransactionData] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  const viewTransactions = async (registrationId) => {
    try {
      const response = await feeTransactionsApi.getByRegistration(
        registrationId,
      );
      setTransactionData(response.data);
      setShowTransactionModal(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      notificationCtx.show({
        message: "Failed to fetch transaction details.",
        stat: "error",
      });
    }
  };

  return (
    <div
      className="modal fade show"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(25, 25, 35, 0.65)",
        zIndex: 10500,
        padding: "24px",
      }}
      tabIndex="-1"
    >
      {/* =========================
      STUDENT DETAILS MODAL
  ========================== */}
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        style={{
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          maxHeight: "92vh",
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: "#ffffff",
            border: "none",
            borderRadius: "22px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            maxHeight: "92vh",
          }}
        >
          {/* Header */}
          <div
            className="modal-header"
            style={{
              padding: "24px 30px",
              backgroundColor: "#d5d1fd",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "1.5px",
                  color: "#77727d",
                  marginBottom: "4px",
                }}
              >
                STUDENT MANAGEMENT
              </div>

              <h3
                className="modal-title mb-0"
                style={{
                  fontWeight: "700",
                  fontSize: "26px",
                  color: "#292631",
                }}
              >
                STUDENT DETAILS
              </h3>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={props.onClose}
              aria-label="Close"
            />
          </div>

          {/* Body */}
          <div
            className="modal-body"
            style={{
              overflowY: "auto",
              padding: "26px 30px 30px",
            }}
          >
            {usercourses && usercourses.length > 0 ? (
              <>
                {/* =========================
                STUDENT INFORMATION
            ========================== */}
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "16px",
                    padding: "20px 24px",
                    marginBottom: "26px",
                    border: "1px solid #ece9e5",
                    boxShadow: "0 3px 12px rgba(0,0,0,0.04)",
                  }}
                >
                  {isListed && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        marginBottom: "16px",
                        padding: "12px 14px",
                        borderRadius: "12px",
                        backgroundColor: isBlackListed ? "#f2c4c4" : "#f2e6c9",
                        boxShadow: `inset 6px 0 0 0 ${
                          isBlackListed ? "#1f1b2e" : "#8a8f9c"
                        }`,
                        color: "#654343",
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            marginBottom: "4px",
                          }}
                        >
                          {isBlackListed ? "Black listed" : "Grey listed"}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: 1.45,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {listStatus?.listReason?.trim() ||
                            "No reason recorded."}
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{
                      borderBottom: "1px solid #eee",
                      paddingBottom: "12px",
                    }}
                  >
                    <div>
                      <h5
                        className="mb-1"
                        style={{
                          fontWeight: "700",
                          color: "#292631",
                        }}
                      >
                        {usercourses[0].name}
                      </h5>

                      {/* <span
                        style={{
                          fontSize: "12px",
                          color: "#77727d",
                        }}
                      >
                        Student Information
                      </span> */}
                    </div>

                    {/* <div
                      style={{
                        backgroundColor: "#f3f1f7",
                        borderRadius: "10px",
                        padding: "7px 12px",
                        fontSize: "12px",
                        color: "#625d75",
                        fontWeight: "600",
                      }}
                    >
                      {usercourses.length}{" "}
                      {usercourses.length === 1
                        ? "Registration"
                        : "Registrations"}
                    </div> */}
                    <button
                      type="button"
                      onClick={() => {
                        props.usercourses[0]
                          ? viewTransactions(props.usercourses[0].id)
                          : notificationCtx.show({
                              message:
                                "No registration found for this student.",
                              stat: "error",
                            });
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 10px",
                        border: "1px solid #ddd9f7",
                        borderRadius: "8px",
                        backgroundColor: "#faf9fe",
                        color: "#625d75",
                        fontSize: "11px",
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i
                        className="fa-solid fa-receipt"
                        style={{
                          fontSize: "11px",
                          color: "#77718e",
                        }}
                      />
                      View Transactions
                    </button>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6 col-lg-3">
                      <div
                        style={{
                          backgroundColor: "#fafafa",
                          borderRadius: "10px",
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            textTransform: "uppercase",
                            letterSpacing: "0.7px",
                            marginBottom: "4px",
                          }}
                        >
                          Email
                        </div>

                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#333",
                            wordBreak: "break-word",
                          }}
                        >
                          {usercourses[0].email}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <div
                        style={{
                          backgroundColor: "#fafafa",
                          borderRadius: "10px",
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            textTransform: "uppercase",
                            letterSpacing: "0.7px",
                            marginBottom: "4px",
                          }}
                        >
                          Phone
                        </div>

                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {usercourses[0].phone}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <div
                        style={{
                          backgroundColor: "#fafafa",
                          borderRadius: "10px",
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            textTransform: "uppercase",
                            letterSpacing: "0.7px",
                            marginBottom: "4px",
                          }}
                        >
                          Parent Phone
                        </div>

                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {usercourses[0].parentsPhoneNumber || "—"}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-3">
                      <div
                        style={{
                          backgroundColor: "#fafafa",
                          borderRadius: "10px",
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            textTransform: "uppercase",
                            letterSpacing: "0.7px",
                            marginBottom: "4px",
                          }}
                        >
                          City
                        </div>

                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {usercourses[0].city}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =========================
                REGISTERED COURSES
            ========================== */}
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5
                        className="mb-1"
                        style={{
                          fontWeight: "700",
                          color: "#292631",
                        }}
                      >
                        REGISTERED COURSES (
                        {usercourses.reduce(
                          (total, registration) =>
                            total + (registration.courses?.length || 0),
                          0,
                        )}
                        )
                      </h5>

                      {/* <span
                        style={{
                          fontSize: "12px",
                          color: "#888",
                        }}
                      >
                        Manage the student's course registrations
                      </span> */}
                    </div>

                    {canEdit && (
                    <button
                      className="btn"
                      onClick={() => OpenAddNewCourseModal()}
                      style={{
                        backgroundColor: "#bff1db",
                        borderColor: "#bff1db",
                        borderRadius: "10px",
                        padding: "9px 16px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#253d32",
                      }}
                    >
                      <i className="fa-solid fa-plus me-2" />
                      Add Course
                    </button>
                    )}
                  </div>

                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "16px",
                      border: "1px solid #ece9e5",
                      boxShadow: "0 3px 12px rgba(0,0,0,0.04)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        overflowX: "auto",
                        width: "100%",
                      }}
                    >
                      <table
                        className="table align-middle mb-0"
                        style={{
                          minWidth: "850px",
                          borderCollapse: "separate",
                          borderSpacing: 0,
                        }}
                      >
                        <thead>
                          <tr>
                            {columnKeys.map((key) => (
                              <th
                                key={key}
                                style={{
                                  backgroundColor: "#d5d1fd",
                                  color: "#2f2c38",
                                  fontWeight: "700",
                                  padding: "14px 16px",
                                  textAlign: "center",
                                  fontSize: "12px",
                                  letterSpacing: "0.7px",
                                  borderBottom: "1px solid #cbc6ee",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {key.toUpperCase()}
                              </th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          {usercourses.map((obj) =>
                            obj.courses?.map((course) => (
                              <tr key={course.id}>
                                <td
                                  style={{
                                    padding: "15px 16px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#333",
                                    borderBottom: "1px solid #f0efed",
                                  }}
                                >
                                  {course.title || "Unknown"}
                                </td>

                                <td
                                  style={{
                                    padding: "15px 16px",
                                    fontSize: "13px",
                                    color: "#555",
                                    textAlign: "center",
                                    borderBottom: "1px solid #f0efed",
                                  }}
                                >
                                  {course.Teacher?.name || "Unknown"}
                                </td>

                                <td
                                  style={{
                                    padding: "15px 16px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #f0efed",
                                  }}
                                >
                                  {(() => {
                                    const activeEnrollment = [
                                      "enrolled",
                                      "pending",
                                    ].includes(
                                      String(course.status || "").toLowerCase(),
                                    );
                                    // Never copy registration-level status onto inactive courses.
                                    const feeStatus = activeEnrollment
                                      ? course.feePaymentStatus ||
                                        (course.status === "pending"
                                          ? "pending"
                                          : null)
                                      : null;
                                    const label =
                                      feeStatus === "partially_paid"
                                        ? "Partially Paid"
                                        : feeStatus === "unpaid"
                                          ? "Unpaid"
                                          : feeStatus === "paid"
                                            ? "Paid"
                                            : feeStatus === "pending"
                                              ? "Pending enrollment"
                                              : "—";
                                    const isPaid = feeStatus === "paid";
                                    const isPartial =
                                      feeStatus === "partially_paid";
                                    const isUnpaid = feeStatus === "unpaid";
                                    return (
                                      <span
                                        style={{
                                          display: "inline-block",
                                          padding: "5px 10px",
                                          borderRadius: "8px",
                                          fontSize: "11px",
                                          fontWeight: "600",
                                          backgroundColor: isPaid
                                            ? "#bff1db"
                                            : isPartial
                                              ? "#f9e1be"
                                              : isUnpaid
                                                ? "#f2c4c4"
                                                : "#f3f1f7",
                                          color: isPaid
                                            ? "#355c4a"
                                            : isPartial
                                              ? "#62503a"
                                              : isUnpaid
                                                ? "#6b3f3f"
                                                : "#625d75",
                                        }}
                                      >
                                        {label}
                                      </span>
                                    );
                                  })()}
                                </td>

                                <td
                                  style={{
                                    padding: "15px 16px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #f0efed",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "5px 10px",
                                      borderRadius: "8px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      backgroundColor:
                                        course.status === "enrolled"
                                          ? "#bff1db"
                                          : course.status === "pending"
                                            ? "#f9e1be"
                                            : "#f3f1f7",
                                      color: "#333",
                                    }}
                                  >
                                    {course.status || "Unknown"}
                                  </span>

                                  {course.comment && (
                                    <div
                                      style={{
                                        marginTop: "6px",
                                        fontSize: "11px",
                                        color: "#888",
                                        maxWidth: "180px",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                      }}
                                    >
                                      {course.comment}
                                    </div>
                                  )}
                                </td>

                                <td
                                  style={{
                                    padding: "15px 16px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #f0efed",
                                  }}
                                >
                                  {course.status === "enrolled" ||
                                  course.status === "pending" ? (
                                    canEdit ? (
                                    <div
                                      className="d-flex justify-content-center"
                                      style={{
                                        gap: "7px",
                                      }}
                                    >
                                      <button
                                        className="btn"
                                        style={{
                                          backgroundColor: "#f2c4c4",
                                          borderColor: "#f2c4c4",
                                          borderRadius: "9px",
                                          padding: "7px 13px",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          color: "#443333",
                                        }}
                                        onClick={() => openDropModal(obj, course)}
                                      >
                                        Drop
                                      </button>

                                      <button
                                        className="btn"
                                        style={{
                                          backgroundColor: "#bff1db",
                                          borderColor: "#bff1db",
                                          borderRadius: "9px",
                                          padding: "7px 13px",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          color: "#253d32",
                                        }}
                                        onClick={() =>
                                          OpenTransferCourseModal(course.id)
                                        }
                                      >
                                        Transfer
                                      </button>
                                    </div>
                                    ) : (
                                      <span style={{ color: "#aaa6b4" }}>—</span>
                                    )
                                  ) : canEdit ? (
                                    <button
                                      className="btn"
                                      style={{
                                        backgroundColor: "#bff1db",
                                        borderColor: "#bff1db",
                                        borderRadius: "9px",
                                        padding: "7px 18px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#253d32",
                                      }}
                                      onClick={() => {
                                        setcomment(course.comment || "");
                                        setregtoadd(obj);
                                        setcoursetoadd(course.id);
                                        setAddBackIsReenroll(
                                          course.status === "transferred",
                                        );
                                        setshowadd(true);
                                      }}
                                    >
                                      {course.status === "transferred"
                                        ? "Re-enroll"
                                        : "Add-back"}
                                    </button>
                                  ) : (
                                    <span style={{ color: "#aaa6b4" }}>—</span>
                                  )}
                                </td>
                              </tr>
                            )),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                  minHeight: "350px",
                  color: "#77727d",
                }}
              >
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    backgroundColor: "#f3f1f7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <i
                    className="fa-solid fa-user-slash"
                    style={{
                      fontSize: "22px",
                      color: "#77727d",
                    }}
                  />
                </div>

                <h5
                  style={{
                    fontWeight: "600",
                    color: "#444",
                  }}
                >
                  No Registered Courses
                </h5>

                <p
                  style={{
                    fontSize: "13px",
                    color: "#888",
                    marginBottom: "20px",
                  }}
                >
                  This student currently has no course registrations.
                </p>

                {/* <button
                  className="btn"
                  style={{
                    backgroundColor: "#bff1db",
                    borderColor: "#bff1db",
                    borderRadius: "10px",
                    padding: "9px 18px",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                  onClick={() => OpenAddNewCourseModal()}
                >
                  <i className="fa-solid fa-plus me-2" />
                  Add New Course
                </button> */}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* =========================================================
      DROP COURSE MODAL
  ========================================================== */}
      {canEdit && showdrop && (
        <div
          className="modal show"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(25,25,35,0.45)",
            zIndex: 10600,
            padding: "20px",
          }}
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{
              width: "100%",
              maxWidth: "500px",
              margin: "auto",
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#ffffff",
                border: "none",
                borderRadius: "18px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #ebe7e2",
                  padding: "20px 24px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      fontWeight: "600",
                      letterSpacing: "1px",
                      marginBottom: "3px",
                    }}
                  >
                    COURSE ACTION
                  </div>

                  <h5
                    className="modal-title mb-0"
                    style={{
                      fontWeight: "700",
                    }}
                  >
                    DROP COURSE
                  </h5>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setshowdrop(false)}
                  aria-label="Close"
                />
              </div>

              <div
                className="modal-body"
                style={{
                  padding: "24px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#fafafa",
                    borderRadius: "10px",
                    padding: "13px 15px",
                    marginBottom: "18px",
                    fontSize: "13px",
                    color: "#555",
                  }}
                >
                  {dropPreviewLoading ? (
                    "Loading refund preview…"
                  ) : dropPreview ? (
                    <>
                      <div>
                        Course paid:{" "}
                        <strong>
                          PKR {Number(dropPreview.paidCourse || 0).toLocaleString()}
                        </strong>
                      </div>
                      <div>
                        Suggested refund:{" "}
                        <strong>
                          PKR{" "}
                          {Number(
                            dropPreview.suggestedRefund || 0,
                          ).toLocaleString()}
                        </strong>{" "}
                        ({dropPreview.daysRemaining}/{dropPreview.daysTotal}{" "}
                        days remaining)
                      </div>
                    </>
                  ) : (
                    "Preview unavailable."
                  )}
                </div>

                {Number(dropPreview?.paidCourse || 0) <= 0 ? (
                  <div
                    style={{
                      backgroundColor: "#fff5f5",
                      border: "1px solid #f2c4c4",
                      borderRadius: "10px",
                      padding: "13px 15px",
                      fontSize: "13px",
                      color: "#8b4a4a",
                      marginBottom: "20px",
                    }}
                  >
                    <i className="fa-solid fa-circle-info me-2" />
                    No paid amount on this course. Refund is not available.
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#fafafa",
                      borderRadius: "10px",
                      padding: "13px 15px",
                      marginBottom: "18px",
                    }}
                  >
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="refundToggle"
                        checked={refund}
                        onChange={() => {
                          const next = !refund;
                          setrefund(next);
                          if (next && dropPreview) {
                            setrefundamount(
                              Number(dropPreview.suggestedRefund) || 0,
                            );
                          }
                        }}
                      />

                      <label
                        className="form-check-label"
                        htmlFor="refundToggle"
                        style={{
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        Add refund
                      </label>
                    </div>
                  </div>
                )}

                {refund && (
                  <>
                    <div className="mb-3">
                      <label
                        htmlFor="refund"
                        className="form-label"
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        Refund Amount
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        id="refund"
                        value={refundamount}
                        onChange={(e) =>
                          setrefundamount(
                            parseInt(
                              e.target.value.replace(/[^0-9]/g, ""),
                              10,
                            ) || 0,
                          )
                        }
                        placeholder="Enter refund amount"
                      />
                    </div>
                    <div className="mb-3">
                      <label
                        htmlFor="refundPaymentMode"
                        className="form-label"
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        Refund payment mode
                      </label>
                      <select
                        id="refundPaymentMode"
                        className="form-select"
                        value={refundPaymentMode}
                        onChange={(e) => setRefundPaymentMode(e.target.value)}
                      >
                        <option value="cash">Cash</option>
                        <option value="online">Online</option>
                      </select>
                    </div>
                  </>
                )}

                <div
                  style={{
                    backgroundColor: "#fafafa",
                    borderRadius: "10px",
                    padding: "13px 15px",
                    marginBottom: "18px",
                  }}
                >
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="clearRemainingPaid"
                      checked={clearRemainingPaid}
                      onChange={() =>
                        setClearRemainingPaid(!clearRemainingPaid)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="clearRemainingPaid"
                      style={{ fontSize: "13px", fontWeight: "500" }}
                    >
                      Also clear remaining paid on this course
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="writeOffResiduals"
                      checked={writeOffResiduals}
                      onChange={() =>
                        setWriteOffResiduals(!writeOffResiduals)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="writeOffResiduals"
                      style={{ fontSize: "13px", fontWeight: "500" }}
                    >
                      Write off residual due gaps on this course
                    </label>
                  </div>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="comment"
                    className="form-label"
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    Comment
                  </label>

                  <textarea
                    className="form-control"
                    id="comment"
                    rows="4"
                    value={comment}
                    onChange={(e) => setcomment(e.target.value)}
                    placeholder="Add a note about this action..."
                    style={{
                      resize: "vertical",
                    }}
                  />
                </div>

                <button
                  className="btn"
                  disabled={mutating || dropPreviewLoading}
                  onClick={() => {
                    dropCourse(courseToDrop, regtodrop.id);
                  }}
                  style={{
                    backgroundColor: "#f2c4c4",
                    borderColor: "#f2c4c4",
                    width: "100%",
                    borderRadius: "10px",
                    padding: "11px",
                    fontWeight: "600",
                    fontSize: "13px",
                  }}
                >
                  {mutating ? "Working..." : "Confirm Drop"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTransactionModal && (
        <div
          className="modal fade show"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 10500,
            padding: "20px",
          }}
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            style={{
              width: "100%",
              maxWidth: "1100px",
              margin: "auto",
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#ffffff",
                border: "none",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
              }}
            >
              {/* Header */}
              <div
                className="modal-header"
                style={{
                  padding: "22px 26px",
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  backgroundColor: "#d5d1fd",
                }}
              >
                <div>
                  <h5
                    className="modal-title mb-1"
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#2f2c35",
                    }}
                  >
                    Transaction Details
                  </h5>

                  {/* <small
                    style={{
                      color: "#6f6a72",
                      fontSize: "13px",
                    }}
                  >
                    View transaction records for this registration
                  </small> */}
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTransactionModal(false)}
                  aria-label="Close"
                />
              </div>

              {/* Body */}
              <div
                className="modal-body"
                style={{
                  padding: "24px 26px 28px",
                  overflowY: "auto",
                  maxHeight: "75vh",
                }}
              >
                {transactionData.length > 0 ? (
                  <TransactionsViewer data={transactionData} />
                ) : (
                  <div
                    className="text-center"
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "15px",
                      padding: "60px 20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        color: "#625d75",
                        marginBottom: "15px",
                      }}
                    >
                      <i className="fa-solid fa-receipt" />
                    </div>

                    <h5
                      style={{
                        marginBottom: "6px",
                        fontWeight: "600",
                        color: "#333",
                      }}
                    >
                      No Transactions so far
                    </h5>

                    <p
                      style={{
                        margin: 0,
                        color: "#777",
                        fontSize: "14px",
                      }}
                    >
                      No transaction records are available for this
                      registration.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* =========================================================
          ADD COURSE MODAL
          ========================================================== */}
      {canEdit && showadd && (
        <div
          className="modal show"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(25,25,35,0.45)",
            zIndex: 10600,
            padding: "20px",
          }}
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{
              width: "100%",
              maxWidth: "500px",
              margin: "auto",
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#ffffff",
                border: "none",
                borderRadius: "18px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid #ebe7e2",
                  padding: "20px 24px",
                }}
              >
                <h5
                  className="modal-title"
                  style={{
                    fontWeight: "700",
                  }}
                >
                  {addBackIsReenroll ? "RE-ENROLL COURSE" : "ADD-BACK COURSE"}
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setshowadd(false)}
                  aria-label="Close"
                />
              </div>

              <div
                className="modal-body"
                style={{
                  padding: "24px",
                }}
              >
                <div className="mb-4">
                  <label
                    htmlFor="comment"
                    className="form-label"
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    Comment
                  </label>

                  <textarea
                    className="form-control"
                    id="comment"
                    rows="4"
                    value={comment}
                    onChange={(e) => setcomment(e.target.value)}
                    placeholder="Add a note about this course..."
                  />
                </div>

                <button
                  className="btn"
                  disabled={mutating}
                  onClick={() => {
                    addCourse(courseToadd, regtoadd.id);
                  }}
                  style={{
                    backgroundColor: "#d5d1fd",
                    borderColor: "#d5d1fd",
                    width: "100%",
                    borderRadius: "10px",
                    padding: "11px",
                    fontWeight: "600",
                    fontSize: "13px",
                  }}
                >
                  {mutating
                    ? "Working..."
                    : addBackIsReenroll
                      ? "Confirm Re-enroll"
                      : "Confirm Add-back"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
      ADD NEW COURSE MODAL
  ========================================================== */}
      {canEdit && addNewCourseModalOpen && (
        <div
          className="modal show"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(25,25,35,0.55)",
            zIndex: 10600,
            padding: "20px",
          }}
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            style={{
              width: "100%",
              maxWidth: "850px",
              margin: "auto",
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#ffffff",
                border: "none",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                maxHeight: "85vh",
              }}
            >
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#ffffff",
                  padding: "20px 24px",
                  borderBottom: "1px solid #ebe7e2",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      fontWeight: "600",
                      letterSpacing: "1px",
                      marginBottom: "3px",
                    }}
                  >
                    COURSE MANAGEMENT
                  </div>

                  <h5
                    className="modal-title mb-0"
                    style={{
                      fontWeight: "700",
                    }}
                  >
                    ADD NEW COURSE
                  </h5>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAddNewCourseModalOpen(false)}
                  aria-label="Close"
                />
              </div>

              <div
                className="modal-body"
                style={{
                  overflowY: "auto",
                  padding: "24px",
                }}
              >
                {allActiveCourses.length === 0 ? (
                  <div
                    className="text-center"
                    style={{
                      padding: "50px 20px",
                      color: "#777",
                    }}
                  >
                    <i
                      className="fa-solid fa-book-open"
                      style={{
                        fontSize: "30px",
                        opacity: 0.35,
                        marginBottom: "15px",
                      }}
                    />

                    <h6
                      style={{
                        fontWeight: "600",
                        color: "#555",
                      }}
                    >
                      No active courses available
                    </h6>

                    <p
                      style={{
                        fontSize: "13px",
                        marginBottom: 0,
                      }}
                    >
                      There are currently no courses that can be added.
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "15px",
                      overflow: "hidden",
                      border: "1px solid #ece9e5",
                    }}
                  >
                    <div style={{ overflowX: "auto" }}>
                      <table
                        className="table align-middle mb-0"
                        style={{
                          minWidth: "650px",
                        }}
                      >
                        <thead>
                          <tr>
                            {["Course Title", "Teacher", "Action"].map(
                              (heading) => (
                                <th
                                  key={heading}
                                  style={{
                                    backgroundColor: "#d5d1fd",
                                    padding: "14px",
                                    textAlign: "center",
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    letterSpacing: "0.6px",
                                    borderBottom: "1px solid #cbc6ee",
                                  }}
                                >
                                  {heading.toUpperCase()}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>

                        <tbody>
                          {allActiveCourses.map((course) => (
                            <tr key={course.id}>
                              <td
                                style={{
                                  padding: "14px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  textAlign: "center",
                                  borderBottom: "1px solid #f0efed",
                                }}
                              >
                                {course.title || "Unknown"}
                              </td>

                              <td
                                style={{
                                  padding: "14px",
                                  fontSize: "13px",
                                  textAlign: "center",
                                  borderBottom: "1px solid #f0efed",
                                }}
                              >
                                {course.Teacher?.name || "Unknown"}
                              </td>

                              <td
                                style={{
                                  padding: "14px",
                                  textAlign: "center",
                                  borderBottom: "1px solid #f0efed",
                                }}
                              >
                                <button
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    borderColor: "#bff1db",
                                    borderRadius: "9px",
                                    padding: "7px 16px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                  }}
                                  onClick={() => handleAddNewCourse(course.id)}
                                  disabled={mutating}
                                >
                                  {mutating ? "Working..." : "Add"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
      TRANSFER COURSE MODAL
  ========================================================== */}
      {canEdit && transferCourseModalOpen && (
        <div
          className="modal show"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(25,25,35,0.55)",
            zIndex: 10600,
            padding: "20px",
          }}
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            style={{
              width: "100%",
              maxWidth: "850px",
              margin: "auto",
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#ffffff",
                border: "none",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                maxHeight: "85vh",
              }}
            >
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#ffffff",
                  padding: "20px 24px",
                  borderBottom: "1px solid #ebe7e2",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      fontWeight: "600",
                      letterSpacing: "1px",
                      marginBottom: "3px",
                    }}
                  >
                    COURSE MANAGEMENT
                  </div>

                  <h5
                    className="modal-title mb-0"
                    style={{
                      fontWeight: "700",
                    }}
                  >
                    TRANSFER COURSE
                  </h5>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setTransferCourseModalOpen(false)}
                  aria-label="Close"
                />
              </div>

              <div
                className="modal-body"
                style={{
                  overflowY: "auto",
                  padding: "24px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "11px",
                    padding: "13px 16px",
                    marginBottom: "20px",
                    border: "1px solid #ece9e5",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  <i className="fa-solid fa-circle-info me-2" />
                  The registration will be transferred to the selected course.
                </div>

                {allActiveCourses.length === 0 ? (
                  <div
                    className="text-center"
                    style={{
                      padding: "50px 20px",
                      color: "#777",
                    }}
                  >
                    <i
                      className="fa-solid fa-arrow-right-arrow-left"
                      style={{
                        fontSize: "30px",
                        opacity: 0.35,
                        marginBottom: "15px",
                      }}
                    />

                    <h6
                      style={{
                        fontWeight: "600",
                        color: "#555",
                      }}
                    >
                      No active courses available
                    </h6>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "15px",
                      overflow: "hidden",
                      border: "1px solid #ece9e5",
                    }}
                  >
                    <div style={{ overflowX: "auto" }}>
                      <table
                        className="table align-middle mb-0"
                        style={{
                          minWidth: "650px",
                        }}
                      >
                        <thead>
                          <tr>
                            {["Course Title", "Teacher", "Action"].map(
                              (heading) => (
                                <th
                                  key={heading}
                                  style={{
                                    backgroundColor: "#d5d1fd",
                                    padding: "14px",
                                    textAlign: "center",
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    letterSpacing: "0.6px",
                                    borderBottom: "1px solid #cbc6ee",
                                  }}
                                >
                                  {heading.toUpperCase()}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>

                        <tbody>
                          {allActiveCourses.map((course) => (
                            <tr key={course.id}>
                              <td
                                style={{
                                  padding: "14px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  textAlign: "center",
                                  borderBottom: "1px solid #f0efed",
                                }}
                              >
                                {course.title || "Unknown"}
                              </td>

                              <td
                                style={{
                                  padding: "14px",
                                  fontSize: "13px",
                                  textAlign: "center",
                                  borderBottom: "1px solid #f0efed",
                                }}
                              >
                                {course.Teacher?.name || "Unknown"}
                              </td>

                              <td
                                style={{
                                  padding: "14px",
                                  textAlign: "center",
                                  borderBottom: "1px solid #f0efed",
                                }}
                              >
                                <button
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    borderColor: "#bff1db",
                                    borderRadius: "9px",
                                    padding: "7px 16px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                  }}
                                  onClick={() =>
                                    handleTransferCourse(course.id)
                                  }
                                  disabled={mutating}
                                >
                                  {mutating ? "Working..." : "Transfer"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserCoursesModal;
